import type { GenModelPreset, ModelPresetType } from '@musedrops/shared';
import { fromDbKeys } from '@musedrops/shared';
import { supabase } from '../services/supabase';

export async function getAllModelPresets(): Promise<GenModelPreset[]> {
  const { data, error } = await supabase
    .from('gen_model_presets')
    .select('*')
    .order('name');

  if (error) {
    throw new Error(`Failed to fetch model presets: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<GenModelPreset>(row as Record<string, unknown>)
  );
}

export async function getModelPresetsByType(
  type: ModelPresetType
): Promise<GenModelPreset[]> {
  const { data, error } = await supabase
    .from('gen_model_presets')
    .select('*')
    .eq('type', type)
    .order('name');

  if (error) {
    throw new Error(`Failed to fetch ${type} model presets: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<GenModelPreset>(row as Record<string, unknown>)
  );
}

export async function getModelPreset(
  key: string
): Promise<GenModelPreset | null> {
  const { data, error } = await supabase
    .from('gen_model_presets')
    .select('*')
    .eq('key', key)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch model preset: ${error.message}`);
  }

  return data
    ? fromDbKeys<GenModelPreset>(data as Record<string, unknown>)
    : null;
}

/**
 * Get the debug preset for a specific type.
 * Returns the preset key if a debug preset exists for the type, null otherwise.
 */
export async function getDebugPresetForType(
  type: ModelPresetType
): Promise<string | null> {
  const { data, error } = await supabase
    .from('gen_model_presets')
    .select('key')
    .eq('type', type)
    .eq('is_debug', true)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to fetch debug preset for type ${type}: ${error.message}`
    );
  }

  return data?.key ?? null;
}

/**
 * Check if a preset is marked as a debug preset.
 * Returns true if the preset has is_debug=true.
 */
export async function isPresetDebug(presetKey: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('gen_model_presets')
    .select('is_debug')
    .eq('key', presetKey)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to check if preset ${presetKey} is debug: ${error.message}`
    );
  }

  return data?.is_debug === true;
}

/**
 * Get the type of a preset by its key.
 * Returns the preset type or null if not found.
 */
export async function getPresetType(
  presetKey: string
): Promise<ModelPresetType | null> {
  const { data, error } = await supabase
    .from('gen_model_presets')
    .select('type')
    .eq('key', presetKey)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to get preset type for ${presetKey}: ${error.message}`
    );
  }

  return data?.type ?? null;
}

// Alias for backwards compatibility
export const listModelPresets = getAllModelPresets;
export const listModelPresetsByType = getModelPresetsByType;
